import { toChecksumAddress } from "ethereum-checksum-address";
import { ethers, utils } from "ethers";
import { CHAIN_IDS_TO_NAMES } from "../constant/chains";
import { getPositionRangesFromV3 } from "./factoryV3Api";
import { getUserBorrowAmountFromLogs } from "./lendingPoolApi";
import factoryV3Abi from "../../abi/factoryV3.json";

export const getAutoVaultCumulativeFeeFromGinza = async () => {
  const url = `https://0dtklop9zj.execute-api.ap-northeast-1.amazonaws.com/stag/positions?user_addr=0x252fb0D5e641Aa703447C7A0F1E74CD2587F3B5e`;

  const options = {
    method: "get",
  };
  const result = await fetch(url, options);
  const resultJson = await result.json();
  // Succeed
  if (result.status === 200) {
    const records = resultJson.records;
    const cumulativeFee = records.reduce(
      (pre: number, curr: { PositionInfo: { Fee: string } }) =>
        pre + +curr.PositionInfo.Fee,
      0
    );
    return utils.formatUnits(cumulativeFee, 6);
  }
  // Failed
  throw new Error(resultJson.error);
};

export const getGinzaFactoryIds = async (chainId?: number) => {
  let url = `https://8bhmkpeoqh.execute-api.ap-northeast-1.amazonaws.com/stag/api/v1/diamond_factory`;

  if (chainId) {
    url += `?chain=${CHAIN_IDS_TO_NAMES[chainId].toLowerCase()}`;
  }

  const options = {
    method: "get",
  };
  const result = await fetch(url, options);
  const resultJson = await result.json();
  // Succeed
  if (result.status === 200) {
    return resultJson.data.map((fac: { [key: string]: string }) => fac.ID);
  }
  // Failed
  throw new Error(resultJson.error);
};

export const getGinzaFactoryPositions = async (
  chainId: number,
  type: "open" | "closed"
) => {
  const url = `https://8bhmkpeoqh.execute-api.ap-northeast-1.amazonaws.com/stag/api/v1/diamond_factory/all/positions?limit=100&type=${type}&chain=${CHAIN_IDS_TO_NAMES[
    chainId
  ].toLowerCase()}`;

  const options = {
    method: "get",
  };
  const result = await fetch(url, options);
  const resultJson = await result.json();
  // Succeed
  if (result.status === 200) {
    return {
      records: resultJson.records,
      totalRecord: resultJson.total_record,
    };
  }
  // Failed
  throw new Error(resultJson.error);
};

export const getGinzaFactorySummary = async (id: string) => {
  const url = `https://8bhmkpeoqh.execute-api.ap-northeast-1.amazonaws.com/stag/api/v1/diamond_factory/${id}/summary`;

  const options = {
    method: "get",
  };
  const result = await fetch(url, options);
  const resultJson = await result.json();
  // Succeed
  if (result.status === 200) {
    return resultJson;
  }
  // Failed
  throw new Error(resultJson.error);
};

export const getGinzaUniPoolApr = async (poolAddress: string) => {
  const url = `https://mpulj9l2j7.execute-api.ap-northeast-1.amazonaws.com/stag/api/v1/uniswap_apr/data`;

  const options = {
    method: "get",
  };
  const result = await fetch(url, options);
  const resultJson = await result.json();
  // Succeed
  if (result.status === 200) {
    const matchedItem = resultJson.find(
      (item: { [key: string]: any }) =>
        item.pool.toUpperCase() === poolAddress.toUpperCase()
    );

    return matchedItem ? matchedItem.fee_apr : 0;
  }
  // Failed
  throw new Error(resultJson.error);
};

export const getGinzaBalancesSummary = async (chainId: number) => {
  const url = `https://8bhmkpeoqh.execute-api.ap-northeast-1.amazonaws.com/stag/api/v1/helpers/info/balances_summary?chain=${CHAIN_IDS_TO_NAMES[
    chainId
  ].toLowerCase()}`;

  const options = {
    method: "get",
  };
  const result = await fetch(url, options);
  const resultJson = await result.json();
  // Succeed
  if (result.status === 200) {
    let balanceObj: { [key: string]: { [key: string]: string } } = {};
    const balances = resultJson.data?.Results;
    balances.forEach((balance: { [key: string]: string }) => {
      const symbol = balance.Symbol;
      if (balanceObj[symbol]) {
        balanceObj[symbol] = {
          ...balanceObj[symbol],
          sum: balanceObj[symbol].sum + balance.Amount,
        };
      } else {
        balanceObj[symbol] = {
          symbol: balance.Symbol,
          decimal: balance.Decimal,
          sum: balance.Amount,
        };
      }
    });
    return Object.values(balanceObj);
  }
  // Failed
  throw new Error(resultJson.error);
};

export const getTokenPriceFromBinance = async (symbol: string) => {
  const url = `https://api.binance.com/api/v3/avgPrice?symbol=${
    symbol.toUpperCase() !== "WETH" ? symbol.toUpperCase() : "ETH"
  }USDT`;

  const options = {
    method: "get",
  };
  const result = await fetch(url, options);
  const resultJson = await result.json();
  // Succeed
  if (result.status === 200) {
    return +resultJson.price;
  }
  // Failed
  throw new Error(resultJson.error);
};

export const getGinzaUserReferral = async (userAddress: string) => {
  const url = `https://8bhmkpeoqh.execute-api.ap-northeast-1.amazonaws.com/stag/api/v1/diamond_referrer/query/get_referral?address=${userAddress}&detail=true`;

  const options = {
    method: "get",
  };
  const result = await fetch(url, options);
  const resultJson = await result.json();
  // Succeed
  if (result.status === 200) {
    const { referral, referee, referee_count } = resultJson.data;

    return {
      referralCode: referral?.ReferralCode,
      referree: referee,
      referreeCount: referee_count,
    };
  }
  // Failed
  throw new Error(resultJson.error);
};

export const getGinzaUserReferrer = async (userAddress: string) => {
  const url = `https://8bhmkpeoqh.execute-api.ap-northeast-1.amazonaws.com/stag/api/v1/diamond_referrer/query/get_referrer_info?address=${userAddress}`;

  const options = {
    method: "get",
  };
  const result = await fetch(url, options);
  const resultJson = await result.json();
  // Succeed
  if (result.status === 200) {
    const { referral } = resultJson.data;

    return {
      referrerAddress: referral.Referrer.Address,
      referrerCode: referral.Referrer.ReferralCode,
    };
  }
  // Failed
  throw new Error(resultJson.error);
};

export const getGinzaRewardRecords = async (
  userAddress: string,
  network: string
) => {
  const url = `https://mpulj9l2j7.execute-api.ap-northeast-1.amazonaws.com/stag/api/v1/reward_program/user_reward/${userAddress}?network=${network}`;

  const options = {
    method: "get",
  };
  const result = await fetch(url, options);
  const resultJson = await result.json();
  // Succeed
  if (result.status === 200) {
    return resultJson.data;
  }
  // Failed
  throw new Error(resultJson.error);
};

export const getUserClosedPositions = async (userAddress: string) => {
  let url = `https://0dtklop9zj.execute-api.ap-northeast-1.amazonaws.com/stag/close_positions?page=1&user_addr=${toChecksumAddress(
    userAddress
  )}`;

  const options = {
    method: "get",
  };

  const result = await fetch(url, options);
  const resultJson = await result.json();

  // Succeed
  if (result.status === 200) {
    const { records } = resultJson;

    const parsedPositionInfos = records
      .filter(
        (i: { [key: string]: any }) => Object.keys(i.PositionInfo).length > 0
      )
      .map((position: any) => {
        const {
          PositionId: positionId,
          PositionInfo: positionInfo,
          OpenTime: startTime,
          CloseTime: closeTime,
          OpenPrice: startPrice,
          ClosePrice: closePrice,
          AmountAfterRepaid: amountAfterRepaid,
          CloseEvent: eventName,
          ReserveWant: reserveWantAmount,
        } = position;

        const {
          Fee: fee,
          UpperTick: upperTick,
          LowerTick: lowerTick,
          BorrowedPrincipal: borrowedPrincipal,
          WantAmount: _wantAmount,
          ReserveAmount: reserveAmount,
        } = positionInfo;

        const entryTick = (+upperTick + +lowerTick) / 2;
        const entryPrice = 1.0001 ** entryTick * 10 ** 12;
        const principal = ethers.utils.formatEther(
          borrowedPrincipal.toString()
        );
        const wantAmount = ethers.utils.formatUnits(_wantAmount, 6);
        const endWantAmount = ethers.utils.formatUnits(amountAfterRepaid, 6);
        const borrowRatio = (entryPrice * +principal) / +wantAmount;

        return {
          positionId: positionId || "",
          startWantAmount: wantAmount || "",
          endWantAmount: endWantAmount || "",
          reserveAmount: ethers.utils.formatUnits(reserveAmount, 6) || "",
          fee: ethers.utils.formatUnits(fee, 6) || "",
          upperTickPrice: 1.0001 ** +upperTick * 10 ** 12 || 0,
          lowerTickPrice: 1.0001 ** +lowerTick * 10 ** 12 || 0,
          borrowRatio: borrowRatio.toFixed(1) || "",
          startTime: startTime,
          closeTime: closeTime,
          startPrice: startPrice,
          closePrice: closePrice,
          eventName: eventName,
          reserveWantAmount:
            ethers.utils.formatUnits(reserveWantAmount, 6) || "",
        };
      });

    return parsedPositionInfos;
  }

  // Failed
  throw new Error(resultJson.error);
};

const convertTickToPrice = (tick: number, decimal: number) => {
  return 1.0001 ** +tick * 10 ** decimal;
};

export const getAllUserClosedPositions = async (
  accountAddress: string,
  factoryAddress: string,
  lendingPoolAddress: string,
  wantTokenDecimal: number,
  borrowTokenDecimal: number,
  chainId: number,
  provider: any
) => {
  const diffTokenDecimal = borrowTokenDecimal - wantTokenDecimal;

  const closedPositionsFromGinza = await getUserClosedPositions(accountAddress);
  const existClosedPositionObj: { [key: string]: boolean } = {};
  closedPositionsFromGinza.forEach((p: { positionId: string }) => {
    existClosedPositionObj[p.positionId] = true;
  });

  const filter = {
    address: factoryAddress,
    fromBlock: 0,
  };

  const res = await provider.getLogs(filter);
  const iface = new utils.Interface(factoryV3Abi);

  const parsedEvents = res.map((log: any) => {
    return { blockNumber: log.blockNumber, ...iface.parseLog(log) };
  });

  const userEvents = parsedEvents.filter(
    (e: any) => e.args?.account?.toUpperCase() === accountAddress.toUpperCase()
  );

  const closePositionEvents = userEvents.filter(
    (e: any) =>
      e.name.toUpperCase() === "ClosePosition".toUpperCase() ||
      e.name.toUpperCase() === "Liquidate".toUpperCase() ||
      e.name.toUpperCase() === "StopLoss".toUpperCase()
  );

  const restClosePositionEvent = closePositionEvents.filter(
    (e: any) => !existClosedPositionObj[e.args.positionId]
  );

  const activePositionEvents = userEvents.filter(
    (e: any) => e.name.toUpperCase() === "OpenPosition".toUpperCase()
  );

  const positionInfos = await Promise.all(
    restClosePositionEvent.map(async (history: any) => {
      const t = (await provider.getBlock(history.blockNumber)).timestamp;
      const positionId = history.args.positionId.toString();

      const matchedOpenPositionLog = activePositionEvents.find(
        (e: any) => e.args.positionId.toString() === positionId
      );

      const {
        _borrowId: borrowId,
        blockNumber,
        reserveWantAmount: _reserveWantAmountAtStart,
        startPriceTick,
        startWantAmount: _startWantAmount,
      } = matchedOpenPositionLog?.args;

      const {
        closePriceTick,
        endWantAmount: _endWantAmount,
        reserveWantAmount: _reserveAmountAtEnd,
      } = history?.args;

      const startTime = (await provider.getBlock(blockNumber)).timestamp;

      const startPrice = convertTickToPrice(+startPriceTick, diffTokenDecimal);

      const closePrice = convertTickToPrice(+closePriceTick, diffTokenDecimal);

      const borrowedEth = await getUserBorrowAmountFromLogs(
        lendingPoolAddress,
        +borrowId,
        borrowTokenDecimal,
        provider
      );

      const reserveWantAmount = +utils.formatUnits(
        _reserveWantAmountAtStart,
        wantTokenDecimal
      );

      const startWantAmount = +utils.formatUnits(
        _startWantAmount,
        wantTokenDecimal
      );

      // get ranges from uni v3
      let upperPrice = 0;
      let lowerPrice = 0;
      try {
        const { tickLower, tickUpper } = await getPositionRangesFromV3(
          +positionId,
          chainId
        );
        if (tickLower && tickUpper) {
          upperPrice = convertTickToPrice(+tickUpper, diffTokenDecimal);
          lowerPrice = convertTickToPrice(+tickLower, diffTokenDecimal);
        }
      } catch (error) {
        console.log(error);
      }

      return {
        endWantAmount: utils.formatUnits(_endWantAmount, wantTokenDecimal),
        positionId: positionId,
        reserveAmount: utils.formatUnits(_reserveAmountAtEnd, wantTokenDecimal),
        startWantAmount: startWantAmount,
        closePrice: closePrice,
        eventName: history.name,
        closeTime: t,
        upperTickPrice: upperPrice,
        lowerTickPrice: lowerPrice,
        borrowRatio: ((borrowedEth * +startPrice) / startWantAmount).toFixed(1),
        startTime: startTime,
        startPrice: startPrice,
        reserveWantAmount: reserveWantAmount,
      };
    })
  );

  return [...positionInfos, ...closedPositionsFromGinza].sort(
    (a, b) => b.closeTime - a.closeTime
  );
};
